import { Segmented, Typography } from "antd";
import TinymceEditor from "./TinymceEditor";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import './report-editor.css'
import { useUser } from "../context/UserContext";
import _ from "lodash";
import { useModal } from "../context/ModalContext";
import ReportPDFPreview from "../../app/report/ReportPDFPreview";

// Bileşen dışında: içeride tanımlanınca her render'da yeni tip olur, tutamak sürükleme sırasında yeniden mount edilirdi.
const ResizeHandle = ({ onMouseDown }) => (
    <div
        onMouseDown={onMouseDown}
        style={{
            height: '10px',
            width: '100%',
            cursor: 'row-resize',
            backgroundColor: 'var(--surface-2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '1px solid var(--border)',
            borderTop: '1px solid var(--border)'
        }}
    >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'var(--text-3)' }} />
    </div>
);

export default function ReportEditor({ defaultReportContent, onBulgular, onSonuc, onKarsilastirma, onTeknik, editable = true, disabled = false }) {
    const { t } = useTranslation();
    const { dictionary } = useUser()
    const { openModal, closeModal } = useModal()
    const [height, setHeight] = useState(() => {
        const savedHeights = localStorage.getItem('report-editor-heights');
        return savedHeights ? JSON.parse(savedHeights) : {
            bulgular: 40,
            sonuc: 40,
            teknik: 20,
            karsilastirma: 20
        };
    })

    // Eskiden "Rapor / Diğer" sekmeleriydi; sekme şeridi editörün üstünde ayrı bir satır kaplıyordu.
    // Geçiş artık üstteki bölüm başlığının sağında. "Diğer" (Teknik, Karşılaştırma) sekmedeki gibi ilk
    // seçildiğinde kurulur, sonra gizlenip açık kalır: TinyMCE her geçişte yeniden kurulmasın.
    const [pane, setPane] = useState('report')
    const [otherMounted, setOtherMounted] = useState(false)

    const stopDragRef = useRef(null);

    useEffect(() => {
        localStorage.setItem('report-editor-heights', JSON.stringify(height));
    }, [height]);

    // Sürüklenirken imleç kolayca TinyMCE iframe'inin üstüne geçer; iframe fare olaylarını yutar:
    // boyut değişmez, mouseup kaçar ve tutamak fareye yapışık kalır. Sürükleme boyunca iframe'ler
    // olay almaz (body.re-resizing); bırakma yine de kaçarsa basılı tuşun kalmadığı ilk harekette biter.
    const handleMouseDown = (e, key) => {
        if (e.button !== 0) return;
        e.preventDefault();
        stopDragRef.current?.();

        const startY = e.clientY;
        const startHeight = height[key];

        const onMove = (ev) => {
            if (ev.buttons === 0) {
                stop();
                return;
            }
            // px farkı yaklaşık vh'ye çevrilir (yükseklikler vh cinsinden saklanıyor).
            const deltaVh = ((ev.clientY - startY) / window.innerHeight) * 100;
            setHeight(prev => ({
                ...prev,
                [key]: Math.max(10, startHeight + deltaVh) // En az 10vh
            }));
        };

        const stop = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', stop);
            window.removeEventListener('blur', stop);
            document.body.classList.remove('re-resizing');
            stopDragRef.current = null;
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', stop);
        window.addEventListener('blur', stop);
        document.body.classList.add('re-resizing');
        stopDragRef.current = stop;
    };

    useEffect(() => () => stopDragRef.current?.(), []);

    // first: bölüm grubunun en üstteki başlığı — Rapor/Diğer geçişi onun sağında durur.
    const sectionTitle = (title, first = false) => (
        <div className={first ? 're-section-title' : 're-section-title re-section-title-next'}>
            <Typography.Title level={5}>{title}</Typography.Title>
            {
                first && (
                    <Segmented
                        size={'small'}
                        value={pane}
                        options={[
                            { label: t('Report'), value: 'report' },
                            { label: t('Other'), value: 'other' },
                        ]}
                        onChange={(value) => {
                            if (value === 'other')
                                setOtherMounted(true)
                            setPane(value)
                        }}
                    />
                )
            }
        </div>
    )

    return (
        <>
            <div hidden={pane !== 'report'}>
                {sectionTitle('Bulgular', true)}
                {
                    editable && (
                        <>
                            <TinymceEditor
                                disabled={disabled}
                                dictionary={dictionary}
                                height={`${height.bulgular}vh`}
                                value={defaultReportContent?.Bulgular}
                                handleChange={(data) => {
                                    onBulgular && onBulgular(data)
                                }}
                            />
                            <ResizeHandle onMouseDown={(e) => handleMouseDown(e, 'bulgular')} />
                        </>
                    )
                }

                {sectionTitle(t('Conclusion and Recommendations'))}
                {
                    editable && (
                        <>
                            <TinymceEditor
                                disabled={disabled}
                                dictionary={dictionary}
                                height={`${height.sonuc}vh`}
                                value={defaultReportContent?.SonucVeOneriler}
                                handleChange={(data) => {
                                    onSonuc && onSonuc(data)
                                }}
                            />
                            <ResizeHandle onMouseDown={(e) => handleMouseDown(e, 'sonuc')} />
                        </>
                    )
                }
            </div>

            {
                otherMounted && (
                    <div hidden={pane !== 'other'}>
                        {sectionTitle('Teknik', true)}
                        {
                            editable && (
                                <>
                                    <TinymceEditor
                                        disabled={disabled}
                                        dictionary={dictionary}
                                        height={`${height.teknik}vh`}
                                        value={defaultReportContent?.Teknik}
                                        handleChange={(data) => {
                                            onTeknik && onTeknik(data)
                                        }}
                                    />
                                    <ResizeHandle onMouseDown={(e) => handleMouseDown(e, 'teknik')} />
                                </>
                            )
                        }

                        {sectionTitle(t('Comparison'))}
                        {
                            editable && (
                                <>
                                    <TinymceEditor
                                        disabled={disabled}
                                        dictionary={dictionary}
                                        height={`${height.karsilastirma}vh`}
                                        value={defaultReportContent?.Karsilastirma}
                                        handleChange={(data) => {
                                            onKarsilastirma && onKarsilastirma(data)
                                        }}
                                    />
                                    <ResizeHandle onMouseDown={(e) => handleMouseDown(e, 'karsilastirma')} />
                                </>
                            )
                        }
                    </div>
                )
            }
        </>
    )
}
